import React, { useState } from 'react';
import { notification } from 'antd';
import type { TableProps } from 'antd';
import { IStockReserved } from '@/interfaces/reserved';
import { deleteStockReserved } from '@/adapters/reserved';
import { IDataModal } from '@/interfaces/dataModal';
import DeleteConfirmModal from '@/components/modal/confirm-delete';
import TableComponent from '@/components/table';
import { formatDate, formatNumber } from '@/utils/dataUtils';
import { EditFilled, DeleteFilled } from '@ant-design/icons';

import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { isFullAccess } from '@/utils/permissionUtils';
import { useNavigate } from 'react-router-dom';

interface IProps {
  loading: boolean;
  stockReserved: IStockReserved[];
  onDelete: (id: number) => void;
  onEditData: (item: IStockReserved) => void;
}

const _stockReservedModal: IDataModal<IStockReserved> = {
  open: false,
};

const StockReservedTable = ({ loading, stockReserved, onDelete }: IProps) => {
  const navigate = useNavigate();
  const perFullAccess = isFullAccess();
  dayjs.extend(relativeTime);
  const [deleteModal, setDeleteModal] =
    useState<IDataModal<IStockReserved>>(_stockReservedModal);

  const onDeleteStockReserved = async () => {
    try {
      await deleteStockReserved(Number(deleteModal?.id));
      notification.success({
        message: 'ลบออเดอร์สำเร็จ',
      });
      onDelete(Number(deleteModal?.id || 0));
      setDeleteModal({ ..._stockReservedModal });
    } catch (error) {
      console.log(error);
    }
  };

  const columns: TableProps<IStockReserved>['columns'] = [
    {
      sorter: true,
      title: 'วันที่จองสินค้า',
      dataIndex: 'createDate',
      key: 'createDate',
      width: 140,
      align: 'center',
      className: 'font-medium text-black',
      render: _ => formatDate(_),
    },
    {
      title: 'รหัสลูกค้า',
      dataIndex: 'customerCode',
      key: 'customerCode',
      className: 'font-bold',
    },
    {
      title: 'ลูกค้า',
      dataIndex: 'customerName',
      key: 'customerName',
      className: 'font-bold',
    },
    {
      title: 'IV No.',
      dataIndex: 'ivNo',
      key: 'ivNo',
      align: 'center',
      width: 120,
    },
    {
      title: 'PO No.',
      dataIndex: 'poNo',
      key: 'poNo',
      align: 'center',
      width: 150,
    },
    {
      title: 'รายการจอง',
      dataIndex: 'stockReservedItems',
      key: 'stockReservedItems',
      align: 'center',
      render: (_, rec) => {
        return (
          <div className="flex w-full flex-col">
            {rec.stockReservedItems.map(i => {
              return (
                <div
                  key={i.id}
                  className="flex felx-row justify-between w-full"
                >
                  <div className="flex">{`${i.productCode}-${i.productName}`}</div>
                  <div className="flex flex-row gap-2 font-bold text-start">
                    <div className="w-48">{`LOT : ${i.stockCode}`}</div>
                    <div className="w-48">{`จอง : ${formatNumber(i.quantity || 0, 0)}`}</div>
                    <div className="w-48 text-blue-600">{`ส่งแล้ว : ${formatNumber(i.take || 0, 0)}`}</div>
                  </div>
                </div>
              );
            })}
          </div>
        );
      },
    },
    {
      dataIndex: 'action',
      key: 'action',
      width: 40,
      hidden: !perFullAccess,
      render: (_, rec) => (
        <div className="flex flex-row gap-4 items-center w-full justify-center">
          <DeleteFilled
            className="text-red-700"
            onClick={() =>
              setDeleteModal({
                open: true,
                item: rec,
                id: rec.id,
              })
            }
          />
          <EditFilled
            className="text-blue-500"
            onClick={() => navigate(`/reserved/${rec.id}`)}
          />
        </div>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-3">
      <TableComponent<IStockReserved>
        columns={columns}
        criteria={undefined}
        data={{
          data: stockReserved || [],
          totalRecords: stockReserved?.length,
        }}
        loading={loading}
        onTableChange={undefined}
        scrollLength={1800}
        scroll
        defaultPagination
      />
      <DeleteConfirmModal
        content={`คุณต้องการยกเลิกรายการจอง ใช่หรือไม่`}
        handleCancel={() => {
          setDeleteModal({ ..._stockReservedModal });
        }}
        handleOk={() => {
          onDeleteStockReserved();
        }}
        open={deleteModal?.open}
        title={`คุณต้องการยกเลิกรายการจอง ใช่หรือไม่`}
      />
    </div>
  );
};

export default StockReservedTable;
